/**
 * UserEditDialog — supports two modes against the same record:
 *   - mode='edit'  → PATCH /api/admin/users/{id} (role / lang / is_active)
 *   - mode='reset' → POST  /api/admin/users/{id}/reset-password
 *
 * Dialog UI pivots on ``mode`` so a single component handles both surfaces
 * without duplicating the chrome. BUG-022: HTML constraints + zod constraints
 * paired; submit disabled with inline reason while invalid.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { formatValidationError } from "@/lib/api";
import { TOKENS } from "@/lib/tokens";
import type { Lang, Role, UserOut } from "@/types/api";

import { useResetUserPassword, useUpdateUser } from "./useUsers";

const ROLE_OPTIONS: Role[] = [
  "content_admin",
  "super_admin",
  "hr_manager",
  "hr_recruiter",
  "hiring_manager",
];
const LANG_OPTIONS: Lang[] = ["es", "en", "de"];

const editSchema = z.object({
  role: z.enum([
    "content_admin",
    "super_admin",
    "hr_manager",
    "hr_recruiter",
    "hiring_manager",
  ]),
  preferred_lang: z.enum(["es", "en", "de"]),
  is_active: z.boolean(),
});
const resetSchema = z.object({
  new_initial_password: z.string().min(8).max(128),
});

type EditValues = z.infer<typeof editSchema>;
type ResetValues = z.infer<typeof resetSchema>;

interface Props {
  open: boolean;
  onClose: () => void;
  mode: "edit" | "reset";
  user: UserOut | null;
}

export function UserEditDialog({ open, onClose, mode, user }: Props) {
  return mode === "edit" ? (
    <EditDialog open={open} onClose={onClose} user={user} />
  ) : (
    <ResetDialog open={open} onClose={onClose} user={user} />
  );
}

function EditDialog({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: UserOut | null;
}) {
  const { t } = useTranslation(["users", "common"]);
  const update = useUpdateUser();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting, isDirty },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    mode: "onChange",
    defaultValues: {
      role: user?.role ?? "hr_recruiter",
      preferred_lang: user?.preferred_lang ?? "es",
      is_active: user?.is_active ?? true,
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        role: user.role,
        preferred_lang: user.preferred_lang,
        is_active: user.is_active,
      });
    }
  }, [user, reset]);

  async function onSubmit(values: EditValues) {
    if (!user) return;
    try {
      await update.mutateAsync({ id: user.id, body: values });
      onClose();
    } catch (err) {
      toast.error(formatValidationError(err));
    }
  }

  return (
    <Dialog
      open={open && user !== null}
      onClose={onClose}
      title={t("users:edit_dialog.title")}
      description={user?.username}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="edit-role">
            {t("users:edit_dialog.role_label")}
          </Label>
          <select
            id="edit-role"
            className="w-full rounded-md px-3"
            style={{
              minHeight: "44px",
              border: `1px solid ${TOKENS.borderStrong}`,
              backgroundColor: TOKENS.surfaceElevated,
              color: TOKENS.text,
            }}
            {...register("role")}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {t(`users:roles.${r}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="edit-lang">
            {t("users:edit_dialog.lang_label")}
          </Label>
          <select
            id="edit-lang"
            className="w-full rounded-md px-3"
            style={{
              minHeight: "44px",
              border: `1px solid ${TOKENS.borderStrong}`,
              backgroundColor: TOKENS.surfaceElevated,
              color: TOKENS.text,
            }}
            {...register("preferred_lang")}
          >
            {LANG_OPTIONS.map((l) => (
              <option key={l} value={l}>
                {t(`users:langs.${l}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="edit-active"
            type="checkbox"
            style={{ width: "20px", height: "20px" }}
            {...register("is_active")}
          />
          <Label htmlFor="edit-active">
            {t("users:edit_dialog.is_active_label")}
          </Label>
        </div>

        <div className="flex flex-row justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            style={{ minHeight: "44px" }}
          >
            {t("users:actions.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting || update.isPending}
            style={{
              backgroundColor: TOKENS.primary,
              color: TOKENS.primaryFg,
              minHeight: "44px",
              fontWeight: 600,
            }}
          >
            {update.isPending
              ? t("common:actions.loading")
              : t("users:edit_dialog.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ResetDialog({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: UserOut | null;
}) {
  const { t } = useTranslation(["users", "common"]);
  const reset = useResetUserPassword();
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    mode: "onChange",
    defaultValues: { new_initial_password: "" },
  });

  async function onSubmit(values: ResetValues) {
    if (!user) return;
    try {
      await reset.mutateAsync({ id: user.id, body: values });
      toast.success(t("users:reset_dialog.success"));
      resetForm();
      onClose();
    } catch (err) {
      toast.error(formatValidationError(err));
    }
  }

  return (
    <Dialog
      open={open && user !== null}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title={t("users:reset_dialog.title")}
      description={`${user?.username ?? ""} — ${t("users:reset_dialog.description")}`}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="reset-password">
            {t("users:reset_dialog.new_initial_password_label")}
          </Label>
          <PasswordInput
            id="reset-password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            aria-invalid={errors.new_initial_password ? "true" : "false"}
            {...register("new_initial_password")}
          />
          {errors.new_initial_password && (
            <p className="text-sm mt-1" style={{ color: "#dc2626" }}>
              {t("users:create_dialog.password_min")}
            </p>
          )}
        </div>

        <div className="flex flex-row justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetForm();
              onClose();
            }}
            style={{ minHeight: "44px" }}
          >
            {t("users:actions.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid || isSubmitting || reset.isPending}
            style={{
              backgroundColor: TOKENS.primary,
              color: TOKENS.primaryFg,
              minHeight: "44px",
              fontWeight: 600,
            }}
          >
            {reset.isPending
              ? t("common:actions.loading")
              : t("users:reset_dialog.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
